/**
 * LLM-assisted aggregation result interpretation
 *
 * Narrates patterns in multi-participant aggregation results: convergence,
 * outliers, polarization, and what the spread implies for decision confidence.
 * Helps facilitators and stakeholders who aren't fluent in the math understand
 * what the aggregated scores mean and where alignment is weak.
 *
 * Registry: aggregation_narrator — on-demand narrator. Transport: callFeatureLlm.
 * Advisory only — never changes any aggregated result, override, or decision.
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const AGGREGATION_SYSTEM_PROMPT =
  'You interpret patterns in multi-participant prioritization aggregations for facilitators. Respond with valid JSON only.';

export interface AggregateItemSummary {
  itemTitle: string;
  outputType: 'classification' | 'ranking' | 'scoring';
  majorityCategory?: string;
  categoryVotes?: Record<string, number>;
  consensusStrength?: number;
  averageScore?: number;
  scoreSpread?: number;
  scoreRange?: { min: number; max: number };
}

export interface AggregationContext {
  toolName: string;
  outputType: 'classification' | 'ranking' | 'scoring';
  participantCount: number;
  overrideCount: number;
  items: AggregateItemSummary[];
}

export interface AggregationInterpretation {
  headline: string;
  narrative: string;
  patterns: string[];
  itemsToWatch: Array<{ title: string; concern: string }>;
  facilitatorNote: string;
}

/**
 * Interpret the patterns in a set of aggregated tool results.
 */
export async function interpretAggregation(
  context: AggregationContext
): Promise<AggregationInterpretation | null> {
  if (!isAiCapabilityEnabled('aggregation_narrator') || context.items.length === 0) {
    return null;
  }

  try {
    const content = await callFeatureLlm({
      userPrompt: buildInterpretationPrompt(context),
      systemPrompt: AGGREGATION_SYSTEM_PROMPT,
      conversationId: 'aggregation-interp-' + Date.now(),
      model: DEFAULT_MODEL,
      temperature: 0.4,
      maxTokens: 600,
    });
    if (!content) return null;
    return parseInterpretationResponse(content);
  } catch (error) {
    console.warn('LLM aggregation interpretation failed:', error);
    return null;
  }
}

function buildInterpretationPrompt(context: AggregationContext): string {
  const itemLines = context.items
    .slice(0, 15)
    .map((item) => {
      if (context.outputType === 'classification') {
        const votes = item.categoryVotes
          ? Object.entries(item.categoryVotes)
              .map(([c, n]) => `${c}:${n}`)
              .join(', ')
          : '';
        const consensus = item.consensusStrength != null
          ? ` (${Math.round(item.consensusStrength * 100)}% consensus)`
          : '';
        return `  - "${item.itemTitle}": majority=${item.majorityCategory ?? '?'}${consensus}${votes ? `, votes=[${votes}]` : ''}`;
      } else {
        const spread = item.scoreSpread != null ? ` ±${item.scoreSpread.toFixed(1)} spread` : '';
        const range = item.scoreRange ? ` [${item.scoreRange.min}–${item.scoreRange.max}]` : '';
        return `  - "${item.itemTitle}": avg=${item.averageScore?.toFixed(1) ?? '?'}${spread}${range}`;
      }
    })
    .join('\n');

  return `You are a prioritization facilitator reviewing aggregated results from a multi-participant tool session.

Tool: "${context.toolName}"
Output type: ${context.outputType}
Participants: ${context.participantCount}
Facilitator overrides: ${context.overrideCount}

Aggregated results:
${itemLines}

Your task: Interpret these aggregation patterns for the facilitator. Look for:
- Items with strong convergence vs. high spread/polarization
- Surprising patterns (e.g. high scoring outliers, unusual category splits)
- Items where the facilitator should pay extra attention before finalizing
- What the overall pattern suggests about group alignment

Return JSON only, no markdown or extra text:
{
  "headline": "One sentence on the overall picture",
  "narrative": "2-3 sentences on the main patterns across all items",
  "patterns": ["2-3 notable patterns (each as a string)"],
  "itemsToWatch": [
    {
      "title": "exact item title",
      "concern": "1 sentence on why this item deserves extra scrutiny"
    }
  ],
  "facilitatorNote": "One concrete suggestion for what the facilitator should do next"
}

Keep language plain and direct. Maximum 2 items in itemsToWatch.`;
}

function parseInterpretationResponse(content: string): AggregationInterpretation | null {
  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;

    const parsed = JSON.parse(jsonMatch[0]);
    if (typeof parsed.headline !== 'string') return null;

    return {
      headline: parsed.headline.trim(),
      narrative: typeof parsed.narrative === 'string' ? parsed.narrative.trim() : '',
      patterns: Array.isArray(parsed.patterns)
        ? parsed.patterns.filter((p: unknown) => typeof p === 'string').slice(0, 4)
        : [],
      itemsToWatch: Array.isArray(parsed.itemsToWatch)
        ? parsed.itemsToWatch
            .filter(
              (w: unknown) =>
                w &&
                typeof w === 'object' &&
                typeof (w as { title?: unknown }).title === 'string' &&
                typeof (w as { concern?: unknown }).concern === 'string'
            )
            .slice(0, 3)
            .map((w: Record<string, string>) => ({
              title: w.title.trim(),
              concern: w.concern.trim(),
            }))
        : [],
      facilitatorNote:
        typeof parsed.facilitatorNote === 'string' ? parsed.facilitatorNote.trim() : '',
    };
  } catch (e) {
    console.warn('Parse aggregation interpretation failed:', e);
    return null;
  }
}
